import React, { useState } from 'react';
import cx from 'classnames';
import { find } from 'lodash';
import { Collapse } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';

import Contents from '@magoosh/lib/contentful_contents';
import Footer from '@magoosh/organisms/footer';
import { paths } from 'config/path_helpers';

import { useQueries } from '@schools/app/queries';
import CategoryProfileList from '@schools/app/components/category_profile_list';
import CategoryProfileListTrigger from '@schools/app/components/category_profile_list/trigger';
import MetricsList from '@schools/app/components/metrics_list';
import MetricsSelect from '@schools/app/components/metrics_select';
import PageOverlay from '@schools/app/components/page_overlay';
import ProfileCardSet from '@schools/app/components/profile_card_set';
import StatRankingTable from '@schools/app/components/stat_rankings_table';
import profileGroups from '@schools/app/utilities/profile_groups';

import colors from '@magoosh/lib/styles/colors.module.scss';
import MagooshLogo from '@magoosh/assets/magoosh-logo-purple.png';
import sharedStyle from '../style.module.scss';

interface Props {
  preview?: boolean;
}

const Metric: React.FC<Props> = (props) => {
  let { categorySlug, slug } = useParams();
  const q = useQueries();
  const [profileSelectIsOpen, setProfileSelectIsOpen] = useState<boolean>(false);

  const schoolProfileCategoryQuery = q.schoolProfileCategoryQuery(categorySlug);
  const category = schoolProfileCategoryQuery.data;

  const schoolProfileMetricQuery = q.schoolProfileMetricQuery(slug, categorySlug);
  const metric = schoolProfileMetricQuery.data;

  // Can this be a chared component, rather than in each page?
  const toggleProfileSelect = (value) => {
    if (value) {
      document.body.classList.add(sharedStyle.overflowHidden);
    } else {
      document.body.classList.remove(sharedStyle.overflowHidden);
    }

    setProfileSelectIsOpen(value);
  };

  return (
    <div className="d-flex flex-column" style={{ minHeight: '100vh' }}>
      {
        category && metric && (
          <Helmet>
            {metric.seoMetaDescription && <meta name="description" content={metric.seoMetaDescription} />}
            <title>{metric.name} for {category.name} - Rankings</title>
          </Helmet>
        )
      }

      {/* TODO - can this be shared? */}
      <div className={cx(sharedStyle.fixedNavbar, { [sharedStyle.in]: profileSelectIsOpen })}>
        <div className="container align-items-center d-flex u-padding-V-m">
          <div>
            <a href="https://magoosh.com">
              <img alt="Magoosh" className={sharedStyle.logo} src={MagooshLogo} />
            </a>
          </div>

          <div className="flex-1">
            <div className="d-flex align-items-center justify-content-end" style={{ gap: 20 }}>
              {
                category && (
                  <div className="hidden-xs">
                    <Link
                      className="small text-body text-no-decoration text-underdash"
                      to={paths.admissions.schoolProfileCategory(category.slug)}
                    >
                      {category.name}
                    </Link>
                  </div>
                )
              }

              <CategoryProfileListTrigger
                currentProfile={null}
                isOpen={profileSelectIsOpen}
                onTrigger={() => toggleProfileSelect(!profileSelectIsOpen)}
              />
            </div>
          </div>
        </div>

        <Collapse in={profileSelectIsOpen}>
          <div className={sharedStyle.categoryProfileListOverlay}>
            <CategoryProfileList
              category={category}
              className="u-padding-B-l"
              preview={props.preview}
            />
          </div>
        </Collapse>
      </div>

      <div className="flex-1" style={{ position: 'relative' }}>
        {<PageOverlay in={profileSelectIsOpen} onClick={() => toggleProfileSelect(false)} />}

        <div className="container">
          <div>
            <h1 className="u-margin-A-n" style={{ color: colors.grayDarker }}>
              <strong>{metric?.name} Overview and Rankings</strong>
            </h1>
            <h4>{category?.name}</h4>
          </div>

          <div className="visible-xs">
            <hr />
            <strong>Metrics and Rankings</strong>
            <MetricsSelect
              category={category}
              currentMetric={metric}
              metrics={category?.metrics}
              preview={props.preview}
            />
          </div>

          {
            metric?.contents && (
              <div className="u-margin-T-l">
                <Contents contents={metric?.contents} />
              </div>
            )
          }

          <div className={cx(sharedStyle.contents, 'd-flex')}>
            <div className={cx(sharedStyle.contentsSections, 'd-flex flex-column flex-1')}>
              {
                category && metric && (
                  <StatRankingTable
                    categorySlug={category.slug}
                    metricSlug={metric.slug}
                  />
                )
              }
            </div>

            <div className={sharedStyle.contentsSidebar}>
              <div className="hidden-xs">
                <h4>Metrics and Rankings</h4>
                <MetricsList
                  category={category}
                  currentMetric={metric}
                  metrics={category?.metrics}
                  preview={props.preview}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="u-margin-T-xl">
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Metric;
